import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FileText, Download, Copy, ExternalLink, FileDown, Image as ImageIcon, Sparkles, Maximize2, Edit3, Eye, CheckCircle, MoreHorizontal, Save, RefreshCw, X, ArrowLeft, FolderOpen, XCircle } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Loading } from '../ui/Loading';
import { Alert } from '../ui/Alert';
import { webhookService, projectService, apiCancelTokens } from '../../services/api';
import { downloadFile, copyToClipboard } from '../../utils/helpers';
import { formatHtml } from '../../utils/formatHtml';
// import { RichTextEditor } from './RichTextEditor'; // Not used
import { ArticlesList } from './ArticlesList';
import { ArticleViewer } from './ArticleViewer';
import { WordPressOptimizer } from './WordPressOptimizer';
import { ArticleBlockEditor } from './ArticleBlockEditor';
import type { Brief, ResearchResult, Article, ArticleTypeId } from '../../types';

interface ArticleGeneratorProps {
  brief: Brief;
  researchResults: ResearchResult[];
  onComplete: (article: Article) => void;
  projectId?: string;
  onBack?: () => void;
  setIsProcessing?: (isProcessing: boolean) => void;
  styleAnalysis?: string | null;
  // Props para tipo de artículo
  articleType?: ArticleTypeId;
  comparisonUrls?: string[];
  comparisonItems?: Array<{
    url: string;
    title: string;
    source: 'wp-page' | 'wp-post' | 'external';
    wpId?: number;
    excerpt?: string;
    featuredImage?: string;
  }>;
  mainSourceUrl?: string;
  wpCredentials?: { url: string; user: string; appPassword: string } | null;
  // CTA fields
  ctaText?: string;
  ctaUrl?: string;
  // Title mode
  isTitleStrict?: boolean;
}

export const ArticleGenerator: React.FC<ArticleGeneratorProps> = ({
  brief,
  researchResults,
  onComplete,
  projectId,
  onBack,
  setIsProcessing,
  styleAnalysis,
  articleType = 'wikipedia',
  comparisonUrls = [],
  comparisonItems = [],
  mainSourceUrl = '',
  wpCredentials = null,
  ctaText,
  ctaUrl,
  isTitleStrict
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [article, setArticle] = useState<Article | null>(null);
  const [activeTab, setActiveTab] = useState<'preview' | 'html' | 'images' | 'meta'>('preview');
  const [copySuccess, setCopySuccess] = useState(false);
  const [metaTags, setMetaTags] = useState<{ [key: string]: string }>({});
  const [isEditingMeta, setIsEditingMeta] = useState(false);
  const [generatedImages, setGeneratedImages] = useState<string[]>([]);
  const [isGeneratingIA, setIsGeneratingIA] = useState(false);
  const [selectedGeneratedImages, setSelectedGeneratedImages] = useState<Set<number>>(new Set());

  const [externalImages, setExternalImages] = useState<string[]>([]);
  const [isFetchingExternal, setIsFetchingExternal] = useState(false);
  const [selectedExternalImages, setSelectedExternalImages] = useState<Set<number>>(new Set());
  const [, setSelectionOrder] = useState<{ type: 'generated' | 'external', index: number }[]>([]);
  const [isGeneratingInfographics, setIsGeneratingInfographics] = useState(false);


  // Custom Search State
  const [showCustomSearch, setShowCustomSearch] = useState(false);
  const [customSearchTerm, setCustomSearchTerm] = useState('');

  // Zoom / Preview State
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const [isEditMode] = useState(false); // setIsEditMode not used currently
  const [editingContent, setEditingContent] = useState('');
  const [isSaved, setIsSaved] = useState(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Estados para mostrar artículos generados
  const [showArticlesList, setShowArticlesList] = useState(false);
  const [selectedArticleToView, setSelectedArticleToView] = useState<any>(null);
  const [wordpressStatus, setWordPressStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // WordPress Optimizer State
  const [showWordPressOptimizer, setShowWordPressOptimizer] = useState(false);
  const [imagesForOptimization, setImagesForOptimization] = useState<string[]>([]);
  const [projectDetailsForOptimizer, setProjectDetailsForOptimizer] = useState<any>(null);

  // Image Modification State
  const [modifyingImageUrl, setModifyingImageUrl] = useState<string | null>(null);
  const [modificationPrompt, setModificationPrompt] = useState<string>('');

  // Estados para el modal de programación - DESHABILITADO
  // const [showScheduleModal, setShowScheduleModal] = useState(false);
  // const [scheduleDate, setScheduleDate] = useState('');
  // const [scheduleTime, setScheduleTime] = useState('');

  // Usar useRef para prevenir doble ejecución en React StrictMode
  const hasGeneratedRef = useRef(false);
  const isGeneratingRef = useRef(false);

  // State for cancellation
  const [isCancelled, setIsCancelled] = useState(false);

  // Handle cancel button click
  const handleCancelGeneration = useCallback(() => {
    setIsCancelled(true);
    apiCancelTokens.cancelArticleGeneration();
  }, []);

  const generateArticle = async () => {
    // Protección TRIPLE contra ejecución múltiple
    if (hasGeneratedRef.current) {

      return;
    }

    if (isGeneratingRef.current) {

      return;
    }

    // VALIDACIÓN CRÍTICA: Verificar que tenemos los datos necesarios
    if (!brief || !researchResults || researchResults.length === 0) {
      alert('Error: Faltan datos para generar el artículo. Por favor, completa el Brief y la Investigación primero.');
      return;
    }

    // Marcar INMEDIATAMENTE antes de cualquier operación async

    hasGeneratedRef.current = true;
    isGeneratingRef.current = true;

    isGeneratingRef.current = true;
    if (setIsProcessing) setIsProcessing(true);

    setIsGenerating(true);

    try {
      // Extraer keywords del brief si existen
      const keywords = [brief?.mainKeyword, ...(brief?.secondaryKeywords || [])].filter(Boolean);

      // Para artículos comparativos, extraer y enriquecer items primero
      let enrichedComparisonItems: any[] = [];
      if (articleType === 'comparative' && comparisonItems && comparisonItems.length > 0) {
        try {
          const extractionResult = await webhookService.extractComparisonItems(
            comparisonItems,
            brief?.title || brief?.mainKeyword || 'Artículo comparativo',
            wpCredentials
          );
          if (extractionResult.success && extractionResult.items) {
            enrichedComparisonItems = extractionResult.items;
          }
        } catch (extractError) {
          // Continuar con items sin enriquecer
          enrichedComparisonItems = comparisonItems.map(item => ({
            ...item,
            summary: item.excerpt || '',
            features: [],
            suggestedAnchor: item.title
          }));
        }
      }

      const generatedArticle = await webhookService.generateArticle(
        brief,
        researchResults,
        undefined, // additionalInstructions
        projectId,
        keywords,
        styleAnalysis || undefined,
        undefined, // linkedPages
        undefined, // includeIndex
        undefined, // faqDesplegable
        articleType,
        comparisonUrls,
        mainSourceUrl,
        undefined, // wordCount
        enrichedComparisonItems.length > 0 ? enrichedComparisonItems : undefined,
        undefined, // linkedUrls
        ctaText,
        ctaUrl,
        isTitleStrict
      );


      setArticle(generatedArticle);

      // Si el artículo ya trae imágenes (por ejemplo, de una generación anterior guardada), las establecemos
      if (generatedArticle.images && generatedArticle.images.length > 0) {
        setGeneratedImages(generatedArticle.images);
      }

      // Marcar tema como publicado si tenemos topicId
      if (brief.topicId) {
        try {
          await webhookService.markTopicAsPublished(brief.topicId);
        } catch (err) {
          // Error marking topic as published - silently ignore
        }
      }

      if (onComplete) {
        onComplete(generatedArticle);
      }
    } catch (error) {
      // Handle cancellation
      if (error instanceof Error && error.message === 'CANCELLED') {
        // Reset refs to allow regeneration
        hasGeneratedRef.current = false;
        isGeneratingRef.current = false;
        setIsGenerating(false);
        setIsCancelled(false);
        if (setIsProcessing) setIsProcessing(false);
        return;
      }

      alert('Hubo un error al generar el artículo. Por favor intenta de nuevo.');
    } finally {
      setIsGenerating(false);
      isGeneratingRef.current = false;
      if (setIsProcessing) setIsProcessing(false);
    }
  };

  // Efecto para iniciar la generación automática
  useEffect(() => {
    // Solo generar si no se ha generado antes y tenemos los datos
    if (!hasGeneratedRef.current && brief && researchResults && researchResults.length > 0) {
      generateArticle();
    }
  }, [brief, researchResults]); // Dependencias estables

  // Estado para el nombre del proyecto
  const [projectName, setProjectName] = useState<string>('');

  // Fetch Project Name specifically for context display
  useEffect(() => {
    if (projectId) {
      const fetchProjectName = async () => {
        try {
          const p = await projectService.getProject(projectId);
          if (p && p.name) {
            setProjectName(p.name);
          }
        } catch (e) {
          // Error fetching project name - silently ignore
        }
      };
      fetchProjectName();
    }
  }, [projectId]);

  // Limpia el HTML para que sea igual al que se sube a WordPress
  const cleanHtmlForCopy = (html: string): string => {
    let cleaned = html;

    // 1. Convertir URLs de proxy a URLs originales de WordPress
    // /api/proxy-image?url=https%3A%2F%2Fexample.com%2Fimage.png&id=123 → https://example.com/image.png
    cleaned = cleaned.replace(
      /src="\/api\/proxy-image\?url=([^"&]+)(?:&amp;[^"]*|&[^"]*)?" /gi,
      (match, encodedUrl) => {
        try {
          const originalUrl = decodeURIComponent(encodedUrl);
          return `src="${originalUrl}" `;
        } catch {
          return match;
        }
      }
    );
    // También sin espacio al final (para cuando el atributo está pegado a otro)
    cleaned = cleaned.replace(
      /src="\/api\/proxy-image\?url=([^"&]+)(?:&amp;[^"]*|&[^"]*)?"/gi,
      (match, encodedUrl) => {
        try {
          const originalUrl = decodeURIComponent(encodedUrl);
          return `src="${originalUrl}"`;
        } catch {
          return match;
        }
      }
    );

    // 2. Remover H1 completo (WordPress lo genera del título)
    cleaned = cleaned.replace(/<h1[^>]*>[\s\S]*?<\/h1>/gi, '');

    // 3. Limpiar target="_blank" de links internos (que empiezan con #)
    cleaned = cleaned.replace(/<a([^>]*href="#[^"]*"[^>]*)target="_blank"([^>]*)>/gi, '<a$1$2>');
    cleaned = cleaned.replace(/<a([^>]*)target="_blank"([^>]*href="#[^"]*"[^>]*)>/gi, '<a$1$2>');

    // 4. Limpiar rel="noopener noreferrer" de links internos
    cleaned = cleaned.replace(/<a([^>]*href="#[^"]*"[^>]*)rel="noopener noreferrer"([^>]*)>/gi, '<a$1$2>');
    cleaned = cleaned.replace(/<a([^>]*)rel="noopener noreferrer"([^>]*href="#[^"]*"[^>]*)>/gi, '<a$1$2>');

    // 5. Eliminar párrafos vacíos
    cleaned = cleaned.replace(/<p>\s*<\/p>/gi, '');

    // 6. Limpiar espacios dobles en atributos de tags
    cleaned = cleaned.replace(/\s{2,}/g, ' ');

    return cleaned.trim();
  };

  const handleCopy = async () => {
    if (!article) return;
    const rawHtml = article.html || article.content;
    const cleanedHtml = cleanHtmlForCopy(rawHtml);
    const success = await copyToClipboard(cleanedHtml);
    if (success) {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const handleDownload = (format: 'html' | 'txt' | 'docx') => {
    if (!article) return;

    let content = article.content;
    let filename = `articulo - ${brief.mainKeyword.replace(/\s+/g, '-')} `;
    let mimeType = 'text/plain';

    if (format === 'html') {
      const rawHtml = article.html || article.content;
      content = cleanHtmlForCopy(rawHtml);
      filename += '.html';
      mimeType = 'text/html';
    } else if (format === 'docx') {
      const rawHtml = article.html || article.content;
      content = cleanHtmlForCopy(rawHtml);
      filename += '.html';
      mimeType = 'application/msword';
    } else {
      filename += '.txt';
    }

    downloadFile(content, filename, mimeType);
  };

  const handleDownloadPDF = () => {
    if (!article) return;

    let htmlForPrint = article.html || article.content;

    if (!htmlForPrint.trim().startsWith('<h1')) {
      htmlForPrint = `< h1 > ${article.title}</h1 >\n${htmlForPrint} `;
    }

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
  < !DOCTYPE html >
    <html lang="es">
      <head>
        <meta charset="UTF-8">
          <title>${article.title}</title>
          <style>
            body {font - family: system-ui, sans-serif; max-width: 800px; margin: 40px auto; padding: 20px; line-height: 1.6; color: #1f2937; }
            h1 {font - size: 2.5rem; line-height: 1.2; color: #1a202c; margin-bottom: 1.5rem; font-weight: 700; }
            h2 {font - size: 2rem; color: #1a202c; margin-top: 2em; margin-bottom: 1rem; font-weight: 600; }
            h3 {font - size: 1.5rem; color: #1a202c; margin-top: 1.5em; margin-bottom: 0.75rem; font-weight: 600; }
            p {margin - bottom: 1.25em; font-size: 1.125rem; }
            ul, ol {margin - bottom: 1.25em; padding-left: 1.75em; }
            li {margin - bottom: 0.5em; }
            a {color: #3b82f6; text-decoration: none; }
            strong {font - weight: 600; }
            img {max - width: 100%; height: auto; page-break-inside: avoid; }
            figure {margin: 30px 0; text-align: center; page-break-inside: avoid; }
            @media print {
              body {margin: 0; padding: 20px; }
            h1, h2, h3 {page -break-after: avoid; }
            p, ul, ol {orphans: 3; widows: 3; }
              }
          </style>
      </head>
      <body>
        ${htmlForPrint}
        <script>
              window.onload = () => {setTimeout(() => { window.print(); window.close(); }, 500); };
        </script>
      </body>
    </html>
`);
      printWindow.document.close();
    }
  };

  const handleFetchExternalImages = async (term?: string) => {
    const searchTerm = term || brief?.mainKeyword;
    if (!searchTerm) return;

    setIsFetchingExternal(true);
    try {
      const response = await webhookService.fetchExternalImages(searchTerm, 6, article?.session_id) as any;

      let extractedImages: string[] = [];

      // Caso 1: { urls: [...] } - Formato anterior
      if (response.urls && Array.isArray(response.urls)) {
        extractedImages = response.urls;
      }
      // Caso 2: { images: [...] } - Formato estándar
      else if (response.images && Array.isArray(response.images)) {
        extractedImages = response.images;
      }
      // Caso 3: { data: [...] } o { data: "base64" } - Formato binario común
      else if (response.data) {
        if (Array.isArray(response.data)) {
          extractedImages = response.data;
        } else if (typeof response.data === 'string') {
          extractedImages = [response.data];
        }
      }
      // Caso 4: Array directo [...]
      else if (Array.isArray(response)) {
        extractedImages = response.map((item: any) => {
          if (typeof item === 'string') return item;
          return item.url || item.output || item.image || item.src || item.data || null;
        }).filter((url: string | null) => url !== null) as string[];
      }

      if (extractedImages.length > 0) {
        setExternalImages(prev => {
          const newSet = new Set(prev);
          extractedImages.forEach(img => newSet.add(img));
          return Array.from(newSet);
        });
      }
    } catch (error) {
      // Error fetching external images - silently ignore
    } finally {
      setIsFetchingExternal(false);
    }
  };

  const handleStartModification = (img: string) => {
    // Si la imagen es un blob/proxy, la usamos tal cual
    // Si viene del backend como nombre de archivo, normalizamos
    setModifyingImageUrl(img);
    setModificationPrompt('');
  };

  const handleGenerateImages = async (referenceImage?: string, modificationPromptText?: string) => {
    // Si es un evento (ClickEvent), referenceImage será un objeto evento, así que lo ignoramos
    const actualReferenceImg = typeof referenceImage === 'string' ? referenceImage : undefined;

    if (!brief?.mainKeyword) return;

    setIsGeneratingIA(true);

    // Preparar resumen de investigación si existe
    let researchSummary = '';
    if (researchResults && researchResults.length > 0) {
      const firstResult = researchResults[0];
      // Usamos 'topic' y 'synthesis' ya que 'title' y 'content' no existen en ResearchResult
      researchSummary = `Título: ${firstResult.topic} \nContenido: ${firstResult.synthesis?.substring(0, 1000) || ''}...`;
    }

    try {
      const resp = await webhookService.generateImages(
        customSearchTerm || brief.mainKeyword,
        actualReferenceImg,
        researchSummary,
        modificationPromptText,
        'all',
        article?.session_id
      );

      if (resp.images && resp.images.length > 0) {
        // Acumular imágenes en lugar de reemplazar (versión 2)
        setGeneratedImages(prev => {
          // Filtrar duplicados basándonos en si la URL/Base64 ya existe
          const currentSet = new Set(prev);
          const newImages = resp.images.filter(img => !currentSet.has(img));

          if (newImages.length > 0) {
            return [...prev, ...newImages];
          }
          return prev;
        });
      } else {
        alert('La IA no devolvio ninguna imagen. Verifica el prompt o intenta de nuevo.');
      }

      if (resp.infographics && resp.infographics.length > 0) {
        // También agregamos infografías a la misma lista de imágenes generadas
        setGeneratedImages(prev => {
          const currentSet = new Set(prev);
          const newInfos = resp.infographics.filter(img => !currentSet.has(img));
          if (newInfos.length > 0) {
            return [...prev, ...newInfos];
          }
          return prev;
        });
      }
    } catch (error) {
      alert('Error al generar imágenes');
    } finally {
      setIsGeneratingIA(false);
    }
  };

  const handleGenerateInfographics = async () => {
    if (!brief?.mainKeyword) return;

    setIsGeneratingInfographics(true);

    // Preparar resumen de investigación si existe
    let researchSummary = '';
    if (researchResults && researchResults.length > 0) {
      const firstResult = researchResults[0];
      researchSummary = `Título: ${firstResult.topic} \nContenido: ${firstResult.synthesis?.substring(0, 1000) || ''}...`;
    }

    try {
      const resp = await webhookService.generateImages(
        customSearchTerm || brief.mainKeyword,
        undefined,
        researchSummary,
        undefined, // modificationPrompt - assuming 'infographics' meant mode before, so explicitly undefined here
        'infographics', // mode
        article?.session_id
      );

      if (resp.infographics && resp.infographics.length > 0) {
        setGeneratedImages(prev => {
          const currentSet = new Set(prev);
          const newInfos = resp.infographics.filter(img => !currentSet.has(img));
          if (newInfos.length > 0) {
            return [...prev, ...newInfos];
          }
          return prev;
        });
      }
    } catch (error) {
      alert('Error al generar infografía');
    } finally {
      setIsGeneratingInfographics(false);
    }
  };

  const toggleImageSelection = (index: number, type: 'generated' | 'external') => {
    if (type === 'generated') {
      const newSelected = new Set(selectedGeneratedImages);
      if (newSelected.has(index)) {
        newSelected.delete(index);
        setSelectionOrder(prev => prev.filter(item => !(item.type === 'generated' && item.index === index)));
      } else {
        newSelected.add(index);
        setSelectionOrder(prev => [...prev, { type: 'generated', index }]);
      }
      setSelectedGeneratedImages(newSelected);
    } else {
      const newSelected = new Set(selectedExternalImages);
      if (newSelected.has(index)) {
        newSelected.delete(index);
        setSelectionOrder(prev => prev.filter(item => !(item.type === 'external' && item.index === index)));
      } else {
        newSelected.add(index);
        setSelectionOrder(prev => [...prev, { type: 'external', index }]);
      }
      setSelectedExternalImages(newSelected);
    }
  };

  // Función para normalizar formato base64
  const normalizeBase64Image = (imageData: any): string => {
    if (!imageData) return '';

    // Si imageData es un objeto con propiedad 'data'
    if (typeof imageData === 'object' && imageData.data) {
      return normalizeBase64Image(imageData.data);
    }

    // Si no es una string, convertir a string
    if (typeof imageData !== 'string') {
      imageData = String(imageData);
    }

    const base64String = (imageData as string).trim();

    // Si es una URL http/https
    if (base64String.startsWith('http://') || base64String.startsWith('https://')) {
      // Si parece ser una página HTML (como las de Freepik), usar el proxy
      if (base64String.match(/\.(htm|html)$/i) || !base64String.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
        return `/ api / proxy - image ? url = ${encodeURIComponent(base64String)} `;
      }
      return base64String;
    }

    // Limpiar cualquier prefijo existente (con o sin espacios) para evitar duplicación
    // Esto maneja casos como "data: image / png; base64, ..." o duplicados "data:image...data:image..."
    const cleanBase64 = base64String.replace(/^data:\s*image\/[^;]+;\s*base64,\s*/i, '');

    // Si después de limpiar aún parece tener otro prefijo (doble prefijo en el origen), limpiarlo de nuevo
    // Esto arregla el error "data: image / png; base64, data: image / png; base64, ..."
    const finalBase64 = cleanBase64.replace(/^data:\s*image\/[^;]+;\s*base64,\s*/i, '');

    if (finalBase64.startsWith('R0lGOD')) {
      // GIF
      return `data:image/gif;base64,${finalBase64}`;
    }

    if (finalBase64.startsWith('/9j/')) {
      // JPEG
      return `data:image/jpeg;base64,${finalBase64}`;
    }

    // Por defecto, asumir PNG
    return `data:image/png;base64,${finalBase64}`;
  };

  // --- DOWNLOAD HELPER ---
  const handleDownloadImage = async (imageUrl: string, filename: string) => {
    try {
      const normalizedUrl = normalizeBase64Image(imageUrl);

      // Si es base64, crear link directo
      if (normalizedUrl.startsWith('data:')) {
        const link = document.createElement('a');
        link.href = normalizedUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // Si es URL externa, intentar fetch para blob
        try {
          const response = await fetch(normalizedUrl);
          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        } catch (e) {
          // Fallback: abrir en nueva pestaña si falla el download programático (CORS etc)
          window.open(normalizedUrl, '_blank');
        }
      }
    } catch (error) {
      alert('Error al descargar la imagen');
    }
  };

  /* Helpers para schema - no usados actualmente
  const extractSchemaScript = (html: string): string => {
    const match = html.match(/<script type="application\/ld\+json">[\s\S]*?<\/script>/);
    return match ? match[0] : '';
  };

  const stripSchemaScript = (html: string): string => {
    return html.replace(/<script type="application\/ld\+json">[\s\S]*?<\/script>/g, '');
  };
  */


  // insertSelectedImages eliminada - ahora la inserción ocurre en ArticleBlockEditor




  const handleOpenInNewTab = () => {
    if (!article) return;

    let htmlToShow = article.html || article.content;

    // Asegurarse de que el H1 esté visible al inicio
    if (!htmlToShow.trim().startsWith('<h1')) {
      htmlToShow = `< h1 > ${article.title}</h1 >\n${htmlToShow} `;
    }

    const fullHtml = `< !DOCTYPE html >
  <html lang="es">
    <head>
      <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <meta name="description" content="${article.metadata.metaDescription}">
            <title>${article.metadata.metaTitle || article.title}</title>
            <style>
              body {font - family: system-ui, -apple-system, sans-serif; max-width: 800px; margin: 0 auto; padding: 40px 20px; line-height: 1.6; color: #1f2937; }
              h1 {font - size: 2.5rem; line-height: 1.2; color: #1a202c; margin-bottom: 1.5rem; font-weight: 700; }
              h2 {font - size: 2rem; color: #1a202c; margin-top: 2em; margin-bottom: 1rem; font-weight: 600; }
              h3 {font - size: 1.5rem; color: #1a202c; margin-top: 1.5em; margin-bottom: 0.75rem; font-weight: 600; }
              p {margin - bottom: 1.25em; font-size: 1.125rem; }
              ul, ol {margin - bottom: 1.25em; padding-left: 1.75em; }
              li {margin - bottom: 0.5em; }
              a {color: #3b82f6; text-decoration: none; }
              a:hover {text - decoration: underline; }
              strong {font - weight: 600; }
              blockquote {border - left: 4px solid #3b82f6; padding-left: 1em; margin-left: 0; color: #4b5563; }
              img {max - width: 100%; height: auto; border-radius: 8px; margin: 20px 0; }
              figure {margin: 30px 0; text-align: center; }
              figcaption {color: #6b7280; font-size: 0.9em; margin-top: 8px; }
            </style>
          </head>
          <body>
            ${htmlToShow}
          </body>
        </html>`;

    const blob = new Blob([fullHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  };

  // Funciones de edición deshabilitadas temporalmente
  // const handleStartEditing = () => {... };
  // const handleSaveEdit = () => {... };
  // const handleCancelEdit = () => {... };


  /* Funciones de edición deshabilitadas - código preservado para referencia
  const handleLocalContentChange = (newContent: string) => {
    setEditingContent(newContent);
  };

  const toggleEditMode = () => {
    if (isEditMode) {
      setArticle(prevArticle => {
        if (prevArticle) {
          const currentHtml = prevArticle.html || prevArticle.content || '';
          const schema = extractSchemaScript(currentHtml);
          const newHasSchema = /<script type="application\/ld\+json">/.test(editingContent);
          let finalHtml = editingContent;
          if (schema && !newHasSchema) {
            finalHtml = editingContent + '\n' + schema;
          }
          return { ...prevArticle, html: finalHtml, content: finalHtml };
        }
        return prevArticle;
      });
    } else {
      const currentHtml = article?.html || article?.content || '';
      setEditingContent(stripSchemaScript(currentHtml));
    }
    setIsEditMode(!isEditMode);
  };
  */

  // Extraer meta tags del HTML
  const extractMetaTags = useCallback((html: string): { [key: string]: string } => {
    // Regex mejorado para capturar atributos multi-línea y con espaciado flexible
    const metaRegex = /<meta\s+([^>]+)>/gi;
    const metas: { [key: string]: string } = {};
    let match;

    while ((match = metaRegex.exec(html)) !== null) {
      const attributes = match[1];

      // Regex más flexibles para atributos
      const nameMatch = attributes.match(/name=["']?([^"'\s>]+)["']?/i);
      const contentMatch = attributes.match(/content=["']?([^"'>]*)["']?/i); // Permitir content vacío y sin comillas (aunque no es estándar)
      const propertyMatch = attributes.match(/property=["']?([^"'\s>]+)["']?/i);
      const charsetMatch = attributes.match(/charset=["']?([^"'\s>]+)["']?/i);

      if (nameMatch && contentMatch) {
        metas[`name:${nameMatch[1]}`] = contentMatch[1];
      } else if (propertyMatch && contentMatch) {
        metas[`property:${propertyMatch[1]}`] = contentMatch[1];
      } else if (charsetMatch) {
        metas['charset'] = charsetMatch[1];
      }
    }

    return metas;
  }, []);

  // Actualizar meta tags en el HTML
  const updateMetaTagsInHtml = useCallback((html: string, updatedMetas: { [key: string]: string }): string => {
    let updatedHtml = html;

    Object.entries(updatedMetas).forEach(([key, value]) => {
      if (key === 'charset') {
        const charsetRegex = /<meta\s+charset=["'][^"']*["']\s*\/?>/gi;
        if (charsetRegex.test(updatedHtml)) {
          updatedHtml = updatedHtml.replace(charsetRegex, `<meta charset="${value}">`);
        }
      } else if (key.startsWith('name:')) {
        const metaName = key.replace('name:', '');
        const nameRegex = new RegExp(`<meta\\s+name=["']${metaName}["']\\s+content=["'][^"']*["']\\s*\/?>`, 'gi');
        if (nameRegex.test(updatedHtml)) {
          updatedHtml = updatedHtml.replace(nameRegex, `<meta name="${metaName}" content="${value}">`);
        }
      } else if (key.startsWith('property:')) {
        const metaProperty = key.replace('property:', '');
        const propertyRegex = new RegExp(`<meta\\s+property=["']${metaProperty}["']\\s+content=["'][^"']*["']\\s*\/?>`, 'gi');
        if (propertyRegex.test(updatedHtml)) {
          updatedHtml = updatedHtml.replace(propertyRegex, `<meta property="${metaProperty}" content="${value}">`);
        }
      }
    });

    return updatedHtml;
  }, []);

  // Cargar meta tags cuando cambia el artículo
  useEffect(() => {
    if (article) {
      const html = article.html || article.content || '';
      let extracted = extractMetaTags(html);

      // Fallback: Si no hay meta tags en el HTML, usar article.metadata
      // Esto es común cuando el backend devuelve solo el body o el fragmento del artículo
      if (Object.keys(extracted).length === 0 && article.metadata) {
        const fallbackMetas: { [key: string]: string } = {};

        if (article.metadata.metaTitle) fallbackMetas['name:title'] = article.metadata.metaTitle;
        if (article.metadata.metaDescription) fallbackMetas['name:description'] = article.metadata.metaDescription;
        if (article.metadata.keywords && article.metadata.keywords.length > 0) {
          fallbackMetas['name:keywords'] = article.metadata.keywords.join(', ');
        }

        // También intentar mapear propiedades si existen en el objeto raíz (legacy)
        if ((article as any).metaDescription) fallbackMetas['name:description'] = (article as any).metaDescription;

        extracted = fallbackMetas;
      }

      setMetaTags(extracted);
    }
  }, [article, extractMetaTags]);

  // Función para guardar manualmente el artículo
  const handleSaveArticle = async (): Promise<any> => {
    if (!article || !projectId) {
      return null;
    }

    setIsSaving(true);
    try {
      // Asegurar que el artículo lleve sus imágenes
      let finalImages: string[] | undefined = Array.isArray((article as any).images)
        ? (article as any).images
        : undefined;

      if (!finalImages || finalImages.length === 0) {
        const selected = Array.from(selectedGeneratedImages || []).map(idx => normalizeBase64Image(generatedImages[idx]));
        if (selected.length > 0) {
          finalImages = selected;
        } else if (generatedImages && generatedImages.length > 0) {
          finalImages = generatedImages.map(img => normalizeBase64Image(img));
        }
      }

      // Asegurar que el article_type se incluya siempre en el payload
      const basePayload = {
        ...article,
        article_type: articleType // Incluir explícitamente el tipo de artículo seleccionado
      };

      const payload = finalImages && finalImages.length > 0
        ? { ...basePayload, images: finalImages }
        : basePayload;

      // Actualizar estado local con la respuesta del servidor (incluyendo ID)
      const response = await projectService.saveArticle(projectId, payload);

      // La respuesta tiene formato {success: true, article: {... }, result: ... }
      const savedArticleData = response.article || response;

      if (savedArticleData && savedArticleData.id) {
        setArticle(prev => ({ ...prev, ...savedArticleData }));
      }

      // Mostrar confirmación
      setIsSaved(true);
      setShowSaveConfirmation(true);
      setTimeout(() => setShowSaveConfirmation(false), 5000);

      return savedArticleData;
    } catch (error) {
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  const handleAnalyzeImagesForWordPress = async () => {
    if (!article) return;

    // Verificar si el artículo tiene ID (está guardado)
    let currentArticleId = article.id;

    // Si el ID es temporal (generado por el cliente/timestamp), forzar guardado para obtener ID real de DB
    const isTemporaryId = typeof currentArticleId === 'string' && currentArticleId.startsWith('article-');

    if (!currentArticleId || isTemporaryId) {
      // Intentar guardar primero
      const savedArticleData = await handleSaveArticle();
      if (savedArticleData && savedArticleData.id) {
        currentArticleId = savedArticleData.id;
      } else {
        alert('Error: Debes guardar el artículo para poder optimizarlo.');
        return;
      }
    }

    // FETCH PROJECT CREDENTIALS
    let projectDetails = null;
    if (projectId) {
      try {
        const credentials = await projectService.getProjectCredentials(projectId);
        projectDetails = credentials;
      } catch (err) {
        // Error loading project credentials - silently ignore
      }
    }

    // Prioritize editingContent if it exists (user might have edited/moved images but not saved)
    // Fallback to article.html or article.content
    const html = (isEditMode && editingContent) ? editingContent : (article.html || article.content || '');

    // Extract base64 images relative to the generated content
    // We look for src that are base64
    const imgRegex = /<img[^>]+src=["'](data:image\/[^;]+;base64,[^"']+)["'][^>]*>/gi;
    const matches: string[] = [];
    let match;
    while ((match = imgRegex.exec(html)) !== null) {
      if (match[1]) matches.push(match[1]);
    }

    if (matches.length === 0) {
      // Fallback: Check if there are ANY images, maybe they are external URLs
      const anyImgRegex = /<img[^>]+src=["']([^"']+)["'][^>]*>/gi;
      let anyMatch;
      while ((anyMatch = anyImgRegex.exec(html)) !== null) {
        if (anyMatch[1]) matches.push(anyMatch[1]);
      }
    }

    if (matches.length === 0) {
      alert('No se encontraron imágenes en el artículo para optimizar.');
      return;
    }

    // De-duplicate
    const uniqueImages = Array.from(new Set(matches));

    setImagesForOptimization(uniqueImages);

    setProjectDetailsForOptimizer(projectDetails);
    setShowWordPressOptimizer(true);
  };

  if (isGenerating) {
    return (
      <div className="space-y-6">
        {projectName && (
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white shadow-lg mb-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none"></div>
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-yellow-300" />
                <h1 className="text-xl font-bold tracking-tight">Generador de Contenido con IA</h1>
              </div>
              <div className="flex items-center gap-2 text-indigo-100 bg-white/10 w-fit px-3 py-1.5 rounded-full text-sm backdrop-blur-sm border border-white/20">
                <FolderOpen className="w-4 h-4" />
                <span className="font-medium tracking-wide">{projectName}</span>
              </div>
            </div>
          </div>
        )}


        <Card className="text-center py-8">
          <Loading size="lg" text={isCancelled ? "Cancelando..." : "Generando artículo..."} />
          <p className="text-gray-600 mt-4">
            {isCancelled
              ? "Cancelando el proceso de generación..."
              : "Esto puede tomar unos momentos. Estamos creando contenido de calidad para ti."
            }
          </p>

          {/* Cancel Button */}
          <div className="mt-6">
            <Button
              onClick={handleCancelGeneration}
              disabled={isCancelled}
              variant="secondary"
              className="bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 px-6 py-2"
            >
              <XCircle className="w-4 h-4 mr-2" />
              {isCancelled ? 'Cancelando...' : 'Cancelar Generación'}
            </Button>
          </div>
        </Card>

        {/* Modal de Lista de Artículos */}
        {showArticlesList && projectId && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2">
            <div className="w-[90%] max-w-none max-h-[90vh] overflow-y-auto bg-white rounded-lg shadow-2xl">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
                <h2 className="text-xl font-semibold">Artículos Generados</h2>
                <button
                  onClick={() => setShowArticlesList(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <div className="p-4">
                <ArticlesList
                  projectId={projectId}
                  onViewArticle={(article) => {
                    setSelectedArticleToView(article);
                    setShowArticlesList(false);
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Modal de Visor de Artículo */}
        {selectedArticleToView && (
          <ArticleViewer
            article={selectedArticleToView}
            onClose={() => setSelectedArticleToView(null)}
            onBack={() => {
              setSelectedArticleToView(null);
              setShowArticlesList(true);
            }}
          />
        )}
      </div>
    );
  }

  if (!article && !isGenerating) {
    const handleRetry = () => {
      // Resetear todos los flags antes de reintentar
      hasGeneratedRef.current = false;
      isGeneratingRef.current = false;
      setIsGenerating(false);
      generateArticle();
    };

    return (
      <div className="space-y-6">
        {projectName && (
          <div className="bg-gradient-to-r from-red-500 to-pink-600 rounded-xl p-6 text-white shadow-lg mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-yellow-300" />
              <h1 className="text-xl font-bold tracking-tight">Generador de Contenido con IA</h1>
            </div>
            <div className="flex items-center gap-2 text-red-100 bg-white/10 w-fit px-3 py-1.5 rounded-full text-sm backdrop-blur-sm border border-white/20">
              <FolderOpen className="w-4 h-4" />
              <span className="font-medium tracking-wide">{projectName}</span>
            </div>
          </div>
        )}
        {projectId && (
          <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <FolderOpen className="w-6 h-6 text-purple-600" />
                <div>
                  <h3 className="font-semibold text-purple-900">Artículos Anteriores</h3>
                  <p className="text-sm text-purple-700">Revisa los artículos que ya has creado para este proyecto</p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowArticlesList(true)}
                icon={<FolderOpen className="w-4 h-4" />}
              >
                Ver Artículos Generados
              </Button>
            </div>
          </Card>
        )}

        <Card>
          <Alert type="error">
            <div className="space-y-2">
              <p className="font-semibold">No se pudo generar el artículo</p>
              <p className="text-sm">Por favor, verifica:</p>
              <ul className="text-sm list-disc list-inside space-y-1">
                <li>Que el backend esté ejecutándose (puerto 3002)</li>
                <li>Que los webhooks de n8n estén activos</li>
                <li>Que completaste los pasos anteriores (Brief e Investigación)</li>
              </ul>
            </div>
          </Alert>
          <div className="flex space-x-3 mt-4">
            <Button onClick={handleRetry} variant="primary">
              Reintentar Generación
            </Button>
            <Button
              onClick={() => window.location.reload()}
              variant="outline"
            >
              Empezar de Nuevo
            </Button>
          </div>
        </Card>

        {/* Modal de Lista de Artículos */}
        {showArticlesList && projectId && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2">
            <div className="w-[90%] max-w-none max-h-[90vh] overflow-y-auto bg-white rounded-lg shadow-2xl">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
                <h2 className="text-xl font-semibold">Artículos Generados</h2>
                <button
                  onClick={() => setShowArticlesList(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <div className="p-4">
                <ArticlesList
                  projectId={projectId}
                  onViewArticle={(article) => {
                    setSelectedArticleToView(article);
                    setShowArticlesList(false);
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Modal de Visor de Artículo */}
        {selectedArticleToView && (
          <ArticleViewer
            article={selectedArticleToView}
            onClose={() => setSelectedArticleToView(null)}
            onBack={() => {
              setSelectedArticleToView(null);
              setShowArticlesList(true);
            }}
            onArticleUpdate={(updatedArticle) => {
              setSelectedArticleToView(updatedArticle);
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {projectName && (
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white shadow-lg mb-6 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none"></div>
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-yellow-300" />
              <h1 className="text-xl font-bold tracking-tight">Generador de Contenido con IA</h1>
            </div>
            <div className="flex items-center gap-2 text-indigo-100 bg-white/10 w-fit px-3 py-1.5 rounded-full text-sm backdrop-blur-sm border border-white/20">
              <FolderOpen className="w-4 h-4" />
              <span className="font-medium tracking-wide">{projectName}</span>
            </div>
          </div>
        </div>
      )}
      {/* Confirmación de Guardado */}
      {showSaveConfirmation && (
        <Alert type="success">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5" />
            <div>
              <p className="font-medium">¡Artículo Guardado en la Base de Datos!</p>
              <p className="text-sm">El artículo se ha guardado correctamente en tu base de datos MySQL.</p>
            </div>
          </div>
        </Alert>
      )}

      <Card>
        {/* Header con título y estadísticas */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <FileText className="w-6 h-6 text-primary-600" />
            <h2 className="text-xl font-semibold">Artículo Generado</h2>
            {isSaved && (
              <span className="flex items-center space-x-1 text-green-600 bg-green-50 px-3 py-1 rounded-lg text-sm font-medium">
                <CheckCircle className="w-4 h-4" />
                <span>Guardado en BD</span>
              </span>
            )}
            {wordpressStatus === 'success' && (
              <span className="flex items-center space-x-1 text-blue-600 bg-blue-50 px-3 py-1 rounded-lg text-sm font-medium animate-in fade-in">
                <CheckCircle className="w-4 h-4" />
                <span>Enviado a WordPress</span>
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            {/* Botón de guardar manual - ACCIÓN PRINCIPAL */}
            <Button
              variant="primary"
              size="sm"
              onClick={handleSaveArticle}
              disabled={isSaving}
              icon={isSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            >
              {isSaving ? 'Guardando...' : 'Guardar'}
            </Button>

            {/* Menú desplegable para acciones secundarias */}
            <div className="relative group">
              <button className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors shadow-sm">
                <MoreHorizontal className="w-4 h-4" />
                <span>Acciones</span>
              </button>

              {/* Dropdown Menu */}
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-100 hidden group-hover:block z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="p-1">
                  {projectId && (
                    <button
                      onClick={() => setShowArticlesList(true)}
                      className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                    >
                      <FolderOpen className="w-4 h-4 text-gray-500" />
                      Ver Artículos
                    </button>
                  )}
                  <button
                    onClick={handleOpenInNewTab}
                    className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                  >
                    <ExternalLink className="w-4 h-4 text-gray-500" />
                    Abrir en Nueva Pestaña
                  </button>

                  <div className="my-1 border-t border-gray-100"></div>

                  <button
                    onClick={() => handleDownload('html')}
                    className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                  >
                    <Download className="w-4 h-4 text-gray-500" />
                    Descargar HTML
                  </button>
                  <button
                    onClick={handleDownloadPDF}
                    className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                  >
                    <FileDown className="w-4 h-4 text-gray-500" />
                    Descargar PDF
                  </button>

                  <div className="my-1 border-t border-gray-100"></div>

                  <button
                    onClick={() => window.location.reload()}
                    className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                  >
                    <Sparkles className="w-4 h-4" />
                    Empezar de Nuevo
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Estadísticas */}
        <div className="grid grid-cols-3 gap-4 text-center mb-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-primary-600">
              {article?.wordCount || (article?.content?.split(/\s+/).length || 0)}
            </div>
            <div className="text-sm text-gray-600">Palabras</div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-primary-600">
              {article?.readingTime || Math.ceil((article?.content?.split(/\s+/).length || 0) / 200)}
            </div>
            <div className="text-sm text-gray-600">Min. de lectura</div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-primary-600">
              {article?.metadata?.keywords?.length || 0}
            </div>
            <div className="text-sm text-gray-600">Keywords</div>
          </div>
        </div>

        {/* Sistema de Pestañas */}
        <div className="border-b border-gray-200 mb-4">
          <nav className="flex space-x-4">
            <button
              onClick={() => setActiveTab('preview')}
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === 'preview'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
            >
              Vista Previa
            </button>
            <button
              onClick={() => setActiveTab('html')}
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === 'html'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
            >
              HTML
            </button>
            <button
              onClick={() => setActiveTab('images')}
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors relative ${activeTab === 'images'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
            >
              <div className="flex items-center space-x-2">
                <span>Imágenes</span>
                {(generatedImages.length + externalImages.length) > 0 && (
                  <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold text-white bg-primary-600 rounded-full">
                    {generatedImages.length + externalImages.length}
                  </span>
                )}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('meta')}
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === 'meta'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
            >
              Meta
            </button>
          </nav>
        </div>

        {/* Contenido de las Pestañas */}
        <div className="space-y-4">
          {/* Pestaña Vista Previa */}
          {activeTab === 'preview' && article && (
            <div className="border border-gray-100 rounded-lg p-6 bg-white">
              <ArticleBlockEditor
                html={editingContent || article.html || article.content || ''}
                onHtmlChange={(newHtml) => {
                  setEditingContent(newHtml);
                  setArticle(prev => prev ? { ...prev, html: newHtml, content: newHtml } : null);
                }}
                projectId={projectId}
                articleTitle={article.title}
                brief={brief}
              />

              <div className="flex justify-between items-center mt-6 pt-6 border-t border-gray-100">
                <Button
                  variant="outline"
                  onClick={onBack}
                  icon={<ArrowLeft className="w-4 h-4" />}
                >
                  Atrás
                </Button>

                <Button
                  onClick={handleAnalyzeImagesForWordPress}
                  size="lg"
                  className="shadow-lg bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white border-0"
                  icon={<Sparkles className="w-5 h-5" />}
                >
                  Optimizar para WordPress
                </Button>
              </div>
            </div>
          )}

          {/* Modal de Modificación de Imagen */}
          {modifyingImageUrl && (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4 backdrop-blur-sm animate-in fade-in duration-200">
              <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden border border-gray-100">
                <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                    Modificar Imagen con IA
                  </h3>
                  <button
                    onClick={() => setModifyingImageUrl(null)}
                    className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                <div className="p-6 space-y-4">
                  <div className="flex justify-center bg-gray-100 rounded-lg p-2 border border-gray-200">
                    <img
                      src={normalizeBase64Image(modifyingImageUrl)}
                      alt="To modify"
                      className="h-48 object-contain rounded-md shadow-sm"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 block">
                      ¿Qué cambios quieres hacer?
                    </label>
                    <textarea
                      value={modificationPrompt}
                      onChange={(e) => setModificationPrompt(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                        }
                      }}
                      placeholder="Ej: Cambiar el fondo a un atardecer en la playa, hacer que la persona sonría..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 min-h-[100px] resize-none text-sm shadow-sm"
                      autoFocus
                    />
                    <p className="text-xs text-gray-500">
                      La IA usará esta imagen como base y aplicará tus instrucciones.
                    </p>
                  </div>
                </div>

                <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                  <Button
                    variant="ghost"
                    onClick={() => setModifyingImageUrl(null)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="primary"
                    disabled={!modificationPrompt.trim() || isGeneratingIA}
                    isLoading={isGeneratingIA}
                    onClick={() => {
                      setModifyingImageUrl(null);
                      handleGenerateImages(modifyingImageUrl, modificationPrompt);
                    }}
                    icon={<Sparkles className="w-4 h-4" />}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    Generar Variación
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Pestaña HTML */}
          {activeTab === 'html' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-700">Código HTML del Artículo</h3>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const htmlCode = article?.html || article?.content || '';
                      const escapeHtml = (s: string) => s
                        .replace(/&/g, '&amp;')
                        .replace(/</g, '&lt;')
                        .replace(/>/g, '&gt;');
                      const rawHtml = `<!DOCTYPE html>\n<html lang="es">\n<head>\n  <meta charset="UTF-8">\n  <title>Código HTML</title>\n  <meta name="viewport" content="width=device-width, initial-scale=1"/>\n  <style>body{margin:0;padding:16px;background:#fff} pre{white-space:pre;overflow:auto;word-wrap:normal; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; font-size: 12px; line-height: 1.5}</style>\n</head>\n<body>\n  <pre>${escapeHtml(htmlCode)}</pre>\n</body>\n</html>`;
                      const blob = new Blob([rawHtml], { type: 'text/html' });
                      const url = URL.createObjectURL(blob);
                      window.open(url, '_blank');
                    }}
                  >
                    Abrir Código
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopy}
                    icon={<Copy className="w-4 h-4" />}
                  >
                    {copySuccess ? '¡Copiado!' : 'Copiar HTML'}
                  </Button>
                </div>
              </div>
              <div className="w-full h-[700px] border border-gray-700 rounded-lg bg-gray-900 overflow-hidden shadow-inner">
                <textarea
                  className="w-full h-full p-4 bg-gray-900 text-gray-100 font-mono text-sm resize-none focus:outline-none selection:bg-blue-500/30"
                  value={formatHtml(article?.html || article?.content || '')}
                  readOnly
                  spellCheck={false}
                />
              </div>
            </div>
          )}

          {/* Pestaña Imágenes */}
          {/* Pestaña Imágenes */}
          {activeTab === 'images' && (
            <div className="space-y-8">

              {/* SECCIÓN 1: BANCO DE IMÁGENES (EXTERNAS) */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <FolderOpen className="w-5 h-5 mr-2 text-blue-600" />
                      Banco de Imágenes
                    </h3>
                    <p className="text-sm text-gray-500">
                      Busca imágenes de alta calidad relacionadas con tu tema.
                    </p>
                  </div>
                  <div className="flex gap-2 items-center">
                    {showCustomSearch ? (
                      <div className="flex gap-2 animate-in fade-in slide-in-from-right-5 duration-300">
                        <input
                          type="text"
                          value={customSearchTerm}
                          onChange={(e) => setCustomSearchTerm(e.target.value)}
                          placeholder="Términos de búsqueda..."
                          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleFetchExternalImages(customSearchTerm);
                          }}
                        />
                        <Button
                          size="sm"
                          onClick={() => handleFetchExternalImages(customSearchTerm)}
                          disabled={isFetchingExternal || !customSearchTerm}
                          isLoading={isFetchingExternal}
                        >
                          Buscar
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setShowCustomSearch(false)}
                          icon={<X className="w-4 h-4" />}
                        >
                          Cancelar
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Button
                          onClick={() => setShowCustomSearch(true)}
                          variant="secondary"
                          size="sm"
                          icon={<Edit3 className="w-4 h-4" />}
                        >
                          Personalizar Búsqueda
                        </Button>
                        <Button
                          onClick={() => handleFetchExternalImages()}
                          disabled={isFetchingExternal}
                          isLoading={isFetchingExternal}
                          icon={<ExternalLink className="w-4 h-4" />}
                          variant="outline"
                        >
                          {externalImages.length > 0 ? 'Buscar Nuevas' : 'Buscar Imágenes'}
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {isFetchingExternal && (
                  <div className="py-8 text-center">
                    <Loading size="md" text="Buscando imágenes..." />
                  </div>
                )}

                {!isFetchingExternal && externalImages.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {externalImages.map((img, index) => (
                      <div
                        key={`ext-${index}`}
                        className={`relative aspect-video rounded-lg overflow-hidden cursor-pointer border-2 transition-all group ${selectedExternalImages.has(index)
                          ? 'border-blue-500 shadow-md ring-2 ring-blue-200'
                          : 'border-transparent hover:border-gray-300'
                          }`}
                        onClick={() => toggleImageSelection(index, 'external')}
                      >
                        <img
                          src={normalizeBase64Image(img)}
                          alt={`External ${index}`}
                          className="w-full h-full object-cover"
                        />
                        {selectedExternalImages.has(index) && (
                          <div className="absolute top-2 right-2 bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-sm z-10">
                            <CheckCircle className="w-4 h-4" />
                          </div>
                        )}

                        {/* Overlay Actions on Hover */}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <button
                            className="bg-white text-gray-900 hover:bg-gray-100 p-2 rounded-full w-10 h-10 flex items-center justify-center shadow-lg transition-transform hover:scale-110"
                            onClick={(e) => {
                              e.stopPropagation();
                              const imgToSend = normalizeBase64Image(img).startsWith('/api/proxy') ? img : normalizeBase64Image(img);
                              handleStartModification(imgToSend);
                            }}
                            title="Modificar con IA"
                          >
                            <Sparkles className="w-5 h-5 text-purple-600" />
                          </button>
                          <button
                            className="bg-white text-gray-900 hover:bg-gray-100 p-2 rounded-full w-10 h-10 flex items-center justify-center shadow-lg transition-transform hover:scale-110"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewImage(normalizeBase64Image(img));
                            }}
                            title="Ampliar"
                          >
                            <Maximize2 className="w-5 h-5" />
                          </button>
                          <button
                            className="bg-white text-gray-900 hover:bg-gray-100 p-2 rounded-full w-10 h-10 flex items-center justify-center shadow-lg transition-transform hover:scale-110"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadImage(img, `stock-image-${index}.png`);
                            }}
                            title="Descargar"
                          >
                            <Download className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {!isFetchingExternal && externalImages.length === 0 && (
                  <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                    <ImageIcon className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No hay imágenes externas cargadas</p>
                  </div>
                )}
              </div>

              {/* SECCIÓN 2: GENERACIÓN IA (LEGACY) */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <Sparkles className="w-5 h-5 mr-2 text-purple-600" />
                      Generación IA
                    </h3>
                    <p className="text-sm text-gray-500">
                      Crea imágenes e infografías únicas con Inteligencia Artificial.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleGenerateInfographics()}
                      disabled={isGeneratingInfographics || isGeneratingIA}
                      isLoading={isGeneratingInfographics}
                      icon={<FileText className="w-4 h-4" />}
                      variant="outline"
                      className="border-purple-200 text-purple-700 hover:bg-purple-50"
                    >
                      Generar Solo Infografía
                    </Button>
                    <Button
                      onClick={() => handleGenerateImages()}
                      disabled={isGeneratingIA || isGeneratingInfographics}
                      isLoading={isGeneratingIA}
                      icon={<Sparkles className="w-4 h-4" />}
                      variant="primary"
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      {generatedImages.length > 0 ? 'Regenerar Todo' : 'Generar Todo con IA'}
                    </Button>
                  </div>
                </div>

                {isGeneratingIA && (
                  <div className="py-8 text-center">
                    <Loading size="md" text="Generando imágenes con IA..." />
                    <p className="text-xs text-gray-400 mt-2">Esto puede tomar unos segundos</p>
                  </div>
                )}

                {isGeneratingInfographics && (
                  <div className="py-8 text-center">
                    <Loading size="md" text="Creando infografía..." />
                    <p className="text-xs text-gray-400 mt-2">Analizando el resumen de investigación...</p>
                  </div>
                )}

                {!isGeneratingIA && !isGeneratingInfographics && generatedImages.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {generatedImages.map((img, index) => (
                      <div
                        key={`gen-${index}`}
                        className={`relative aspect-video rounded-lg overflow-hidden cursor-pointer border-2 transition-all group ${selectedGeneratedImages.has(index)
                          ? 'border-purple-500 shadow-md ring-2 ring-purple-200'
                          : 'border-transparent hover:border-gray-300'
                          }`}
                        onClick={() => toggleImageSelection(index, 'generated')}
                      >
                        <img
                          src={normalizeBase64Image(img)}
                          alt={`Generated ${index}`}
                          className="w-full h-full object-cover"
                        />
                        {selectedGeneratedImages.has(index) && (
                          <div className="absolute top-2 right-2 bg-purple-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-sm z-10">
                            <CheckCircle className="w-4 h-4" />
                          </div>
                        )}

                        {/* Overlay Actions on Hover */}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <button
                            className="bg-white text-gray-900 hover:bg-gray-100 p-2 rounded-full w-10 h-10 flex items-center justify-center shadow-lg transition-transform hover:scale-110"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewImage(normalizeBase64Image(img));
                            }}
                            title="Ampliar"
                          >
                            <Maximize2 className="w-5 h-5" />
                          </button>
                          <button
                            className="bg-white text-gray-900 hover:bg-gray-100 p-2 rounded-full w-10 h-10 flex items-center justify-center shadow-lg transition-transform hover:scale-110"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadImage(img, `generated-image-${index}.png`);
                            }}
                            title="Descargar"
                          >
                            <Download className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {!isGeneratingIA && generatedImages.length === 0 && (
                  <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                    <Sparkles className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No hay imágenes generadas por IA</p>
                  </div>
                )}
              </div>

              <div className="flex justify-between items-center mt-6 pt-6 border-t border-gray-100">
                <Button
                  variant="outline"
                  onClick={onBack}
                  icon={<ArrowLeft className="w-4 h-4" />}
                >
                  Atrás
                </Button>

                {/* Botón de insertar imágenes eliminado - ahora la inserción ocurre en ArticleBlockEditor */}
              </div>

            </div >
          )}


          {/* Pestaña Meta */}
          {
            activeTab === 'meta' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-gray-700">Etiquetas Meta del Artículo</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {isEditingMeta ? 'Edita las etiquetas meta y los cambios se reflejarán en tiempo real' : 'Visualiza todas las etiquetas meta del HTML'}
                    </p>
                  </div>
                  <Button
                    variant={isEditingMeta ? "primary" : "outline"}
                    size="sm"
                    onClick={() => {
                      if (isEditingMeta) {
                        // Guardar cambios
                        const html = article?.html || article?.content || '';
                        const updatedHtml = updateMetaTagsInHtml(html, metaTags);
                        setArticle(prev => prev ? { ...prev, html: updatedHtml, content: updatedHtml } : prev);
                      }
                      setIsEditingMeta(!isEditingMeta);
                    }}
                    icon={isEditingMeta ? <CheckCircle className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
                  >
                    {isEditingMeta ? 'Guardar Cambios' : 'Editar Metas'}
                  </Button>
                </div>

                {Object.keys(metaTags).length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <p className="text-gray-600">
                      No se encontraron etiquetas meta en el HTML del artículo.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {Object.entries(metaTags).map(([key, value]) => {
                      const type = key === 'charset' ? 'charset' : key.startsWith('name:') ? 'name' : 'property';
                      const displayKey = key.replace('name:', '').replace('property:', '');

                      return (
                        <div key={key} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Tipo de Meta */}
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                                Type
                              </label>
                              <div className="flex items-center">
                                <span className={`px-2 py-1 rounded text-xs font-medium uppercase ${type === 'charset' ? 'bg-orange-100 text-orange-700' :
                                  type === 'name' ? 'bg-blue-100 text-blue-700' :
                                    'bg-purple-100 text-purple-700'
                                  }`}>
                                  {type}
                                </span>
                              </div>
                            </div>

                            {/* Nombre/Propiedad */}
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                                {type === 'charset' ? 'Charset' : type === 'name' ? 'Name' : 'Property'}
                              </label>
                              <input
                                type="text"
                                value={displayKey}
                                disabled
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 text-sm font-mono"
                              />
                            </div>

                            {/* Contenido */}
                            <div className="md:col-span-1">
                              <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                                Content
                              </label>
                              {isEditingMeta ? (
                                <textarea
                                  value={value}
                                  onChange={(e) => setMetaTags({ ...metaTags, [key]: e.target.value })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm resize-none"
                                  rows={2}
                                />
                              ) : (
                                <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700 text-sm break-words">
                                  {value}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Vista previa de cambios */}
                {isEditingMeta && Object.keys(metaTags).length > 0 && (
                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
                      <Eye className="w-4 h-4 mr-2" />
                      Vista Previa de Cambios
                    </h4>
                    <p className="text-sm text-blue-700 mb-3">
                      Los cambios se guardarán cuando hagas clic en "Guardar Cambios" y se verán reflejados en el HTML del artículo.
                    </p>
                    <div className="bg-white rounded border border-blue-200 p-3 max-h-48 overflow-y-auto">
                      <pre className="text-xs font-mono text-gray-700">
                        {Object.entries(metaTags).map(([key, value]) => {
                          if (key === 'charset') {
                            return `<meta charset="${value}">\n`;
                          } else if (key.startsWith('name:')) {
                            const metaName = key.replace('name:', '');
                            return `<meta name="${metaName}" content="${value}">\n`;
                          } else if (key.startsWith('property:')) {
                            const metaProperty = key.replace('property:', '');
                            return `<meta property="${metaProperty}" content="${value}">\n`;
                          }
                          return '';
                        }).join('')}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            )
          }
        </div >
      </Card >

      {/* Modal de Lista de Artículos */}
      {
        showArticlesList && projectId && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white rounded-lg">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
                <h2 className="text-xl font-semibold">Artículos Generados</h2>
                <button
                  onClick={() => setShowArticlesList(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <FileText className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <div className="p-4">
                <ArticlesList
                  projectId={projectId}
                  onViewArticle={(article) => {
                    setSelectedArticleToView(article);
                    setShowArticlesList(false);
                  }}
                />
              </div>
            </div>
          </div>
        )
      }

      {/* Modal de Visor de Artículo */}
      {
        selectedArticleToView && (
          <ArticleViewer
            article={selectedArticleToView}
            projectId={projectId}
            onClose={() => setSelectedArticleToView(null)}
            onBack={() => {
              setSelectedArticleToView(null);
              setShowArticlesList(true);
            }}
          />
        )
      }

      {/* WordPress Optimizer Modal */}
      {showWordPressOptimizer && article && (
        <WordPressOptimizer
          articleId={article.id}
          projectId={projectId}
          articleContent={article.html || article.content}
          articleTitle={typeof article.title === 'object' ? (article.title as any).title || (article.title as any).mainKeyword : article.title}
          // SEO meta fields para plugins como Yoast, Rank Math, AIOSEO
          metaTitle={article.metadata?.metaTitle || article.article_data?.metadata?.metaTitle || (typeof article.title === 'object' ? (article.title as any).title : article.title)}
          metaDescription={article.metadata?.metaDescription || article.article_data?.metadata?.metaDescription || ''}
          images={imagesForOptimization}
          projectDetails={projectDetailsForOptimizer}
          onClose={() => setShowWordPressOptimizer(false)}
          onSuccess={() => {
            setShowWordPressOptimizer(false);
            setWordPressStatus('success');
          }}
        />
      )}

      {/* LIGHTBOX / IMAGE PREVIEW MODAL */}
      {
        previewImage && (
          <div className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4 animate-in fade-in duration-200" onClick={() => setPreviewImage(null)}>
            <div className="relative max-w-[90vw] max-h-[90vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
              <img
                src={normalizeBase64Image(previewImage)}
                alt="Preview"
                className="max-w-full max-h-[85vh] object-contain rounded shadow-2xl"
              />
              <div className="flex gap-4 mt-4">
                <Button
                  variant="primary"
                  className="!bg-white !text-gray-900 border-none hover:!bg-gray-200 shadow-lg font-bold"
                  onClick={() => handleDownloadImage(previewImage, 'image-preview.png')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descargar
                </Button>
                <Button
                  variant="primary"
                  className="!bg-purple-600 !text-white border-none hover:!bg-purple-700 shadow-lg font-bold"
                  onClick={() => {
                    if (previewImage) {
                      setPreviewImage(null);
                      handleStartModification(previewImage);
                    }
                  }}
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Modificar con IA
                </Button>
                <Button
                  variant="ghost"
                  className="text-white hover:bg-white/20 border border-white/30"
                  onClick={() => setPreviewImage(null)}
                >
                  <X className="w-4 h-4 mr-2" />
                  Cerrar
                </Button>
              </div>
              <button
                className="absolute -top-10 right-0 text-white hover:text-gray-300 p-2"
                onClick={() => setPreviewImage(null)}
              >
                <X className="w-8 h-8" />
              </button>
            </div>
          </div>
        )
      }

      {/* Modal de Programación - DESHABILITADO: Usar ArticlesList para programar artículos */}
      {/* {showScheduleModal && (
        <div...>...</div>
      )} */}
    </div >
  );
};





